import { FormEvent, useEffect, useState } from 'react';
import { listAllowedUsers, upsertAllowedUser } from '@/services/user-management.service';
import type { AllowedUser, UserRole } from '@/types';

const UserManagementPanel = () => {
  const [users, setUsers] = useState<AllowedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<UserRole>('ANALYST');
  const [isActive, setIsActive] = useState(true);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await listAllowedUsers();
      setUsers(data);
    } catch (error) {
      console.error('Failed to load allowed users:', error);
      alert('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadUsers();
  }, []);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    try {
      await upsertAllowedUser({
        email,
        role,
        isActive,
      });
      setEmail('');
      setRole('ANALYST');
      setIsActive(true);
      await loadUsers();
    } catch (error: any) {
      console.error('Failed to upsert allowed user:', error);
      alert(error?.message || 'Failed to save user.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="bg-white shadow-lg rounded-xl border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900">User Management</h2>
          <p className="text-sm text-gray-500">
            Super admins can allow or deactivate dashboard users by email.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="name@company.com"
          className="md:col-span-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as UserRole)}
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="ANALYST" className="text-gray-900">Analyst</option>
          <option value="ADMIN" className="text-gray-900">Admin</option>
        </select>
        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          Active
        </label>
        <button
          type="submit"
          disabled={saving}
          className="md:col-span-4 inline-flex justify-center rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save User'}
        </button>
      </form>

      {loading ? (
        <p className="text-sm text-gray-500">Loading users...</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-600">Email</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-600">Role</th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-gray-600">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {users.map((user) => (
                <tr key={user.email}>
                  <td className="px-3 py-2 text-sm text-gray-800">{user.email}</td>
                  <td className="px-3 py-2 text-sm text-gray-700">
                    {user.accessLevel === 'SUPER_ADMIN' ? 'Super Admin' : user.role}
                  </td>
                  <td className="px-3 py-2 text-sm">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                        user.isActive
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {user.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-3 py-4 text-sm text-gray-500">
                    No managed users found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default UserManagementPanel;
